"use client";

import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import { useIsMobile } from "../hooks/use-mobile";
import MainLayout from "../components/MainLayout";
import UserProfile from "../components/profile_components/UserProfile";
import ManageEvents from "../components/profile_components/ManageEvents";
import ManageClassifieds from "../components/profile_components/ManageClassifieds";
import ManageBlockedUsers from "../components/profile_components/ManageBlockedUsers";
import ImagePickerDialog from "../components/profile_components/ImagePickerDialog";
import SidebarContent from "../components/profile_components/SidebarContent";
import { successToast, errorToast } from "../utils/toasters";
import { GetProfile } from "../services/profile";
import { deleteClassified, queryListings } from "../services/classified";
import { jwtDecode } from "jwt-decode";
import {
  getPersonalEvents,
  fetchCommunityEvents,
  fetchCommercialEvents,
} from "../services/events";
import { handlePatchRequest } from "../hooks/api";
import { base64ToFile, isAuthenticated, getUserId } from "../utils/helpers";
import { useNavigate } from "react-router-dom";
import { listReportedUsers } from "../services/reportuser";

function ProfilePage() {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [classifiedData, setClassifiedData] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [blockedUsersData, setBlockedUsersData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Debug counter to force re-renders
  const [debugCounter, setDebugCounter] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProfile() {
      setIsProcessing(true);
      try {
        const profile = await GetProfile();
        setFormData(profile.data);
        setUserData(profile.data);
      } catch (error) {
        console.error("Error while fetching profile", error);
        //errorToast("Error while fetching profile");
      } finally {
        setIsProcessing(false);
      }
    }
    fetchProfile();
  }, []);

  // Fetching listings based on user
  useEffect(() => {
    async function fetchListings() {
      try {
        const token = localStorage.getItem("token");
        const parsedAccess = JSON.parse(token);
        const decoded = jwtDecode(parsedAccess.access);
        const user_id = decoded.userid;
        const payload = { user_id: user_id };
        const listings = await queryListings(payload);
        setClassifiedData(listings.data);
      } catch (error) {
        console.error("error while fetching listisngs", error);
        //errorToast("Error while fetching your listings");
      }
    }
    fetchListings();
  }, []);

  useEffect(() => {
    async function fetchEvents() {
      setLoading(true);
      try {
        const userid = getUserId();
        const [personalRes, communityRes, commercialRes] = await Promise.all([
          getPersonalEvents(),
          fetchCommunityEvents({ userid }),
          fetchCommercialEvents({ userid }),
        ]);

        const allEvents = [
          ...(personalRes?.message || []),
          ...(communityRes?.data || []),
          ...(commercialRes?.message || []),
        ];
        console.log(
          personalRes.message,
          communityRes.data,
          commercialRes.message,
          "all response"
        );
        setEventsData(allEvents);
      } catch (error) {
        console.error("Error while fetching events:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchEvents();
  }, []);

  // Fetch blocked users
  useEffect(() => {
    async function fetchBlockedUsers() {
      try {
        const response = await listReportedUsers();
        setBlockedUsersData(response.data);
      } catch (error) {
        console.error("Error while fetching blocked users:", error);
      }
    }

    fetchBlockedUsers();
  }, []);

  useEffect(() => {}, [isEditing, debugCounter]);

  useEffect(() => {
    async function checkUserAuthentication() {
      if (!isAuthenticated()) {
        localStorage.setItem("target", "/profile");
        navigate("/signin");
      }
    }
    checkUserAuthentication();
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      const payload = new FormData();

      payload.append("username", formData.username);
      payload.append("gender", formData.gender);
      payload.append("mobileno", formData.mobileno);
      payload.append("userloginname", formData.userloginname ?? "");
      payload.append("createddate", formData.createddate);
      payload.append("firstname", formData.firstname);
      payload.append("lastname", formData.lastname);
      if (formData.profileImage) {
        const mimeType = formData.profileImage.match(/data:(.*);base64/)[1];
        const file = base64ToFile(
          formData.profileImage,
          `${formData.username}_profile.jpeg`,
          mimeType
        );
        payload.append("profile_picture", file);
      } else {
        payload.append("avatar_id", formData.avatar_id.toString());
      }
      const editProfileResponse = await handlePatchRequest(
        "/auth/edit-profile",
        payload,
        true
      );
      successToast("UserProfile Details Updated Successfully");
    } catch (error) {
      console.error("Error while saving user data", error);
      errorToast("Error While Updating User Profile");
    } finally {
      setIsProcessing(false);
      setIsEditing(false);
    }
  };

  const handleDiscard = () => {
    setFormData({ ...userData });
    setIsEditing(false);
    setDebugCounter((prev) => prev + 1);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setDebugCounter((prev) => prev + 1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleAvatarClick = () => {
    if (isEditing) {
      setImagePickerOpen(true);
    }
  };

  const closeImagePicker = () => {
    setImagePickerOpen(false);
  };

  const handleImageSelected = (newImage) => {
    setFormData((prev) => ({ ...prev, profileImage: newImage }));
    closeImagePicker();
  };

  /* For classified item deletion */
  const handleDeleteClassifiedItem = async (classifiedId) => {
    setIsProcessing(true);

    try {
      await deleteClassified(classifiedId);
      const updatedData = classifiedData.filter(
        (item) => item.id !== classifiedId
      );
      setClassifiedData(updatedData);
      successToast("Your Product Has Been Successfully Deleted");
    } catch (error) {
      console.error("Error while deleting item", error);
      errorToast("Error while deleting item");
    } finally {
      setIsProcessing(false);
    }
  };
  /* For classified item deletion end*/

  return (
    <MainLayout rs={false} onMenuClick={() => setSidebarOpen(true)} menu={true}>
      {isProcessing && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader2 className="animate-spin" width={64} height={64} />
        </div>
      )}

      <div className="min-h-screen flex font-afacad">
        {/* Mobile Sidebar */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-50 z-50 flex"
            onClick={() => setSidebarOpen(false)}
          >
            <div
              className="w-4/5 max-w-sm bg-white h-full p-4 box-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="m-0 text-xl">Menu</h2>
                <button
                  className="bg-none border-none cursor-pointer"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X size={24} />
                </button>
              </div>
              <SidebarContent
                handleTabChange={handleTabChange}
                activeTab={activeTab}
                setSidebarOpen={setSidebarOpen}
              />
            </div>
          </div>
        )}

        {/* Desktop Sidebar */}
        {!isMobile && (
          <div className="w-64 p-4 bg-white border-r border-gray-300">
            <SidebarContent
              handleTabChange={handleTabChange}
              activeTab={activeTab}
              setSidebarOpen={setSidebarOpen}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4 mt-0">
          {activeTab === "profile" && formData && (
            <UserProfile
              formData={formData}
              isEditing={isEditing}
              handleAvatarClick={handleAvatarClick}
              handleInputChange={handleInputChange}
              handleDiscard={handleDiscard}
              handleSave={handleSave}
              setIsEditing={setIsEditing}
              setDebugCounter={setDebugCounter}
            />
          )}
          {activeTab === "events" && (
            <>
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <svg
                    className="animate-spin h-8 w-8 text-purple-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </div>
              ) : (
                <ManageEvents events={eventsData} loading={loading} />
              )}
            </>
          )}

          {activeTab === "classifieds" && classifiedData && (
            <ManageClassifieds
              handleDeleteClassifiedItem={handleDeleteClassifiedItem}
              classifieds={classifiedData}
            />
          )}

          {activeTab === "blocked-users" && (
            <ManageBlockedUsers
              setIsProcessing={setIsProcessing}
              blockedUsers={blockedUsersData}
            />
          )}
        </div>
      </div>
      {imagePickerOpen && (
        <ImagePickerDialog
          onClose={closeImagePicker}
          onImageSelected={handleImageSelected}
        />
      )}
    </MainLayout>
  );
}

export default ProfilePage;
